<template>
    <div v-if="renderableBloks.length" :class="containerClass">
        <slot name="header" />

        <div class="space-y-5">
            <component
                :is="componentFor(blok)"
                v-for="blok in renderableBloks"
                :key="blok._uid"
                :blok="blok"
                v-bind="$attrs"
            />
        </div>
    </div>
</template>

<script>
import { mapStores } from 'pinia'
import { useTranslationsStore } from '@/stores/translations.js'
import { storyblokService } from '@/services/storyblok.js'
import { BLOCK_COMPONENTS } from './blocks.js'

/**
 * Fetches one Storyblok story and renders its blocks through BLOCK_COMPONENTS.
 * Renders nothing at all — including the `header` slot — when Storyblok is
 * unconfigured, unreachable, the story is missing or it holds no renderable
 * blocks, so a section heading never outlives its content.
 */
export default {
    name: 'StoryblokContent',
    // Attrs (including listeners) belong on the rendered blocks, not the
    // wrapper, so a parent can listen to a block's events directly.
    inheritAttrs: false,
    props: {
        slug: {
            type: String,
            required: true,
        },
        // Field on the story that holds the nestable blocks.
        field: {
            type: String,
            default: 'body',
        },
        containerClass: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            bloks: [],
        }
    },
    computed: {
        ...mapStores(useTranslationsStore),
        renderableBloks() {
            return this.bloks.filter((blok) => this.componentFor(blok))
        },
    },
    watch: {
        // Storyblok serves per-language field values, so refetch on switch.
        'translationsStore.activeLocale': 'loadStory',
        slug: 'loadStory',
    },
    created() {
        this.loadStory()
    },
    methods: {
        componentFor(blok) {
            return BLOCK_COMPONENTS[blok?.component] ?? null
        },
        async loadStory() {
            try {
                const story = await storyblokService.fetchStory(this.slug, {
                    locale: this.translationsStore.activeLocale,
                })
                const bloks = story?.content?.[this.field] ?? []
                this.bloks = Array.isArray(bloks) ? bloks : [bloks]

                const unknown = this.bloks
                    .filter((blok) => !this.componentFor(blok))
                    .map((blok) => blok.component)
                if (unknown.length) {
                    console.warn(
                        `Unregistered Storyblok block(s) in "${this.slug}":`,
                        unknown.join(', '),
                    )
                }
            } catch (error) {
                console.warn(`Storyblok story "${this.slug}" unavailable:`, error)
                this.bloks = []
            }
        },
    },
}
</script>
